import { formatKnots } from '@/lib/format';
import { Dialog, DialogContent, DialogFooter, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useTargetsStore } from '@/lib/store/targets';
import { useAlertsStore } from '@/lib/store/alerts';
import { useAuthStore } from '@/lib/store/auth';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

/**
 * Mockup `.daa-modal-overlay` — full-screen dark-themed alert modal for crit DAA conflicts.
 * Triggers when an `events` payload with `category=DAA, severity=crit, ack_required=true`
 * appears for a target visible in the targets store.
 */
export function DaaConflictModal() {
  const items = useAlertsStore((s) => s.items);
  const ack = useAlertsStore((s) => s.acknowledge);
  const targets = useTargetsStore((s) => s.byId);
  const ackTarget = useTargetsStore((s) => s.acknowledge);
  const user = useAuthStore((s) => s.user);
  const [dismissed, setDismissed] = useState<Set<string>>(() => new Set());

  const active = items.find((i) => i.severity === 'crit' && i.category === 'DAA' && i.ack_required && !i.acknowledged && !dismissed.has(i.event_id));
  const target = active?.conflict_target ? targets[active.conflict_target] : null;
  const [seconds, setSeconds] = useState(active?.ttc_sec ?? 0);

  useEffect(() => {
    if (!active?.ttc_sec) return;
    setSeconds(active.ttc_sec);
    const tid = setInterval(() => setSeconds((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(tid);
  }, [active?.event_id, active?.ttc_sec]);

  if (!active) return null;

  const onAck = () => {
    ack(active.event_id, user?.fullName ?? 'pilot');
    if (active.conflict_target) ackTarget(active.conflict_target);
    toast.success(`Conflict acknowledged — ${active.conflict_target ?? ''}`);
    setDismissed((s) => new Set(s).add(active.event_id));
  };

  return (
    <Dialog open={true} onOpenChange={() => setDismissed((s) => new Set(s).add(active.event_id))}>
      <DialogContent className="bg-[#16131a] text-white border-(--color-red) max-w-md">
        <div className="text-center text-2xl">⚠</div>
        <DialogTitle className="text-center text-[#f87171] text-base font-bold uppercase tracking-wider">
          Traffic Conflict — {active.conflict_target ?? '—'}
        </DialogTitle>
        <div className="text-center font-mono text-2xl font-bold text-[#f87171]">
          {String(Math.floor(seconds / 60)).padStart(2, '0')}:{String(seconds % 60).padStart(2, '0')}
        </div>
        <dl className="font-mono text-[0.846em] space-y-1.5 py-2 border-y border-white/10 my-2">
          <Row label="Distance" value={active.dist_km != null ? `${active.dist_km.toFixed(2)} km` : '—'} crit />
          {target && <>
            <Row label="Closing Speed" value={formatKnots(target.speed_kts)} />
            <Row label="Vert Sep" value={target.vert_sep_ft != null ? `${target.vert_sep_ft} ft` : '—'} crit={target.vert_sep_ft != null && Math.abs(target.vert_sep_ft) < 200} />
            <Row label="Horiz Sep" value={target.horiz_sep_nm != null ? `${target.horiz_sep_nm.toFixed(2)} nm` : '—'} crit={target.horiz_sep_nm != null && target.horiz_sep_nm < 1} />
            <Row label="Trust" value={target.trust} />
            <Row label="Sources" value={target.source_services.join(' · ')} />
          </>}
        </dl>
        <div className="rounded-md bg-[rgba(196,26,26,.12)] border border-[rgba(196,26,26,.35)] p-3 my-2">
          <div className="text-[0.692em] uppercase tracking-wider text-white/45">Recommended Action</div>
          <div className="text-[1em] font-bold mt-0.5">Descend to 30 m AGL · Maintain heading</div>
        </div>
        <DialogFooter className="!justify-stretch gap-2">
          <Button variant="ghost" className="flex-1 bg-transparent text-white/60 border border-white/20 hover:bg-white/5" onClick={() => setDismissed((s) => new Set(s).add(active.event_id))}>
            Reject
          </Button>
          <Button variant="danger" className="flex-1" onClick={onAck}>
            ✓ Acknowledge
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value, crit }: { label: string; value: React.ReactNode; crit?: boolean }) {
  return (
    <div className="flex justify-between">
      <dt className="text-white/45">{label}</dt>
      <dd className={crit ? 'text-[#fbbf24] font-bold' : 'text-white font-semibold'}>{value}</dd>
    </div>
  );
}
